// Nạp YouTube IFrame Player API (https://developers.google.com/youtube/iframe_api_reference)
// đúng một lần cho cả trang. Khai báo kiểu tối thiểu tại chỗ, không thêm gói npm.

export interface YTPlayer {
  loadVideoById(opts: { videoId: string; startSeconds?: number }): void;
  cueVideoById(opts: { videoId: string; startSeconds?: number }): void;
  playVideo(): void;
  pauseVideo(): void;
  stopVideo(): void;
  mute(): void;
  unMute(): void;
  isMuted(): boolean;
  setVolume(volume: number): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  destroy(): void;
}

export interface YTPlayerEvent {
  target: YTPlayer;
  data: number;
}

export interface YTPlayerOptions {
  width?: string | number;
  height?: string | number;
  videoId?: string;
  playerVars?: Record<string, string | number>;
  events?: {
    onReady?: (e: YTPlayerEvent) => void;
    onStateChange?: (e: YTPlayerEvent) => void;
    onError?: (e: YTPlayerEvent) => void;
    onAutoplayBlocked?: (e: YTPlayerEvent) => void;
  };
}

export interface YTNamespace {
  Player: new (el: HTMLElement | string, opts: YTPlayerOptions) => YTPlayer;
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

export const YOUTUBE_IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

let apiPromise: Promise<YTNamespace> | null = null;

/**
 * Trả về `window.YT` khi API sẵn sàng. Gọi nhiều lần chỉ chèn thẻ script một lần. Nếu trang đã
 * có sẵn `window.onYouTubeIframeAPIReady` (thư viện khác), vẫn gọi nó chứ không ghi đè mất.
 * Script lỗi (mất mạng) ⇒ reject và cho phép lần gọi sau thử lại.
 */
export const loadYouTubeIframeApi = (): Promise<YTNamespace> => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.YT && typeof window.YT.Player === 'function') return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<YTNamespace>((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === 'function') {
        try {
          previous();
        } catch {
          // Lỗi của handler cũ không được làm hỏng overlay.
        }
      }
      if (window.YT) resolve(window.YT);
      else reject(new Error('YT missing'));
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${YOUTUBE_IFRAME_API_SRC}"]`
    );
    if (existing) return;
    const script = document.createElement('script');
    script.src = YOUTUBE_IFRAME_API_SRC;
    script.async = true;
    script.onerror = () => {
      apiPromise = null;
      script.remove();
      reject(new Error('Không tải được YouTube IFrame API'));
    };
    document.head.appendChild(script);
  });
  return apiPromise;
};

/** Chỉ dùng trong test: xoá bộ nhớ đệm promise. */
export const resetYouTubeIframeApiForTest = () => {
  apiPromise = null;
};
